/**
 * PricingCard Component
 * Combined Budget + Current Price + Price History in one card
 * Merges the previous separate Budget & Pricing card and PriceHistoryPanel
 */

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';

const PricingCard = ({ project }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const salesForm = project?.salesForm || {};

  // Fetch price history when expanded
  useEffect(() => {
    if (showHistory && priceHistory.length === 0) {
      fetchPriceHistory();
    }
  }, [showHistory]);

  const fetchPriceHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/design-projects/${project.id}/price-history`);
      setPriceHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching price history:', err);
      if (err.response?.status !== 404) {
        setPriceHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  // Format budget range
  const getBudgetDisplay = () => {
    if (salesForm.budgetMin && salesForm.budgetMax) {
      return `${formatCurrency(salesForm.budgetMin)} - ${formatCurrency(salesForm.budgetMax)}`;
    }
    if (salesForm.budgetMin) {
      return `From ${formatCurrency(salesForm.budgetMin)}`;
    }
    if (salesForm.budgetMax) {
      return `Up to ${formatCurrency(salesForm.budgetMax)}`;
    }
    return 'N/A';
  };

  // Calculate price change
  const getPriceChange = (currentPrice, previousPrice) => {
    if (!previousPrice || previousPrice === 0) return null;
    const change = currentPrice - previousPrice;
    const percentRaw = (change / previousPrice) * 100;

    // Cap extreme percentages for display (e.g., when previous price was very small)
    let percentChange;
    if (Math.abs(percentRaw) > 999) {
      percentChange = percentRaw > 0 ? '>999' : '<-999';
    } else {
      percentChange = percentRaw.toFixed(1);
    }

    return { change, percentChange };
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pricing
        </h2>
        {priceHistory.length > 0 || project.packagePriceVersion > 1 ? (
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 min-h-[44px] px-2 -mr-2 touch-manipulation"
          >
            <span className="hidden xs:inline">{showHistory ? 'Hide' : 'View'} History</span>
            <span className="xs:hidden">{showHistory ? 'Hide' : 'History'}</span>
            <svg
              className={`w-5 h-5 transition-transform ${showHistory ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        ) : null}
      </div>

      {/* Main pricing info */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <p className="text-xs sm:text-sm text-gray-500">Budget</p>
          <p className="mt-1 text-sm font-medium text-gray-900">{getBudgetDisplay()}</p>
        </div>
        <div>
          <p className="text-xs sm:text-sm text-gray-500">Estimate</p>
          <p className="mt-1 text-sm font-medium text-gray-900">
            {salesForm.estimatedPackagePrice ? formatCurrency(salesForm.estimatedPackagePrice) : 'N/A'}
          </p>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <p className="text-xs sm:text-sm text-gray-500">Current Price</p>
          <p className="mt-1 text-base sm:text-lg font-semibold text-green-700">
            {formatCurrency(project.currentPackagePrice || 0)}
          </p>
          <p className="text-xs text-gray-400">Version {project.packagePriceVersion || 1}</p>
        </div>
      </div>

      {/* Price History - Collapsible */}
      {showHistory && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {loadingHistory ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading history...</p>
            </div>
          ) : priceHistory.length === 0 ? (
            <p className="text-center py-4 text-sm text-gray-500">No price changes recorded</p>
          ) : (
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {priceHistory.map((item, index) => {
                const previousPrice = index < priceHistory.length - 1 ? priceHistory[index + 1].newPrice : null;
                const priceChange = getPriceChange(item.newPrice, previousPrice);

                return (
                  <div key={item.id || index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(item.newPrice || 0)}
                      </p>
                      {priceChange && (
                        <p className={`text-xs font-medium ${priceChange.change >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {priceChange.change >= 0 ? '+' : ''}{formatCurrency(priceChange.change, false)} ({priceChange.percentChange}%)
                        </p>
                      )}
                      {item.reason && (
                        <p className="text-xs text-gray-500 mt-1">{item.reason}</p>
                      )}
                    </div>
                    <div className="text-right text-xs text-gray-400">
                      <p>{formatDate(item.updatedAt || item.createdAt)}</p>
                      {item.updatedBy && (
                        <p className="mt-0.5">by {item.updatedBy.firstName} {item.updatedBy.lastName}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {priceHistory.length > 1 && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-gray-600">Original Price</p>
                <p className="text-sm font-bold text-gray-900">
                  {formatCurrency(priceHistory[priceHistory.length - 1]?.newPrice || 0)}
                </p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-600">Current Price</p>
                <p className="text-sm font-bold text-gray-900">
                  {formatCurrency(priceHistory[0]?.newPrice || 0)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PricingCard;
